import { useEffect, useRef, useState } from 'react'
import { ExternalLink, FileText, Send, X } from 'lucide-react'
import { Link } from '@tanstack/react-router'
import { PageTitle, Sheet, cx } from '@/components/ui'
import { api } from '@/runtime'
import type { MemberThread } from '@/db/api'
import { useAllPages } from '@/db/hooks'
import { MdLite } from '@/lib/mdlite'
import { PANEL_QUERY } from '@/lib/panel'
import { useKeyboardUp } from '@/hooks/useVisualViewport'
import { useMediaQuery } from '@/hooks/useMediaQuery'
import { useArtifactPanel } from '@/state/panel'
import { ArtifactEditor } from '@/components/ArtifactPanel'
import { DockAside } from '@/components/Dock'

/**
 * A workspace member's own thread with Prime (`/ask`). Her Prime is a separate
 * one that sees and changes only her workspace; the server keeps the thread
 * (she cannot sync it) and says every time that Alfie can read it. What she
 * typed stays in the box until the server has answered.
 */
export const MemberPrimeView = () => {
  const [thread, setThread] = useState<MemberThread | null>(null)
  const [openPage, setOpenPage] = useState('')
  const wide = useMediaQuery(PANEL_QUERY)
  const pages = useAllPages()
  const width = useArtifactPanel((s) => s.width)
  const setWidth = useArtifactPanel((s) => s.setWidth)
  const [text, setText] = useState('')
  const [sending, setSending] = useState('')
  const [error, setError] = useState('')
  const keyboardUp = useKeyboardUp()
  const end = useRef<HTMLDivElement>(null)

  useEffect(() => {
    let live = true
    const load = () =>
      void api
        .memberPrime()
        .then((t) => live && setThread(t))
        .catch(() => {})
    load()
    // a message from Prime lands while the thread is open: look again when the app is back
    const onVisible = () => document.visibilityState === 'visible' && load()
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      live = false
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [])

  useEffect(() => {
    end.current?.scrollIntoView({ block: 'end' })
  }, [thread, sending])

  const send = async (said?: string) => {
    const message = (said ?? text).trim()
    if (!message || sending) return
    setSending(message)
    setError('')
    try {
      setThread(await api.memberPrimeSend(message))
      if (said === undefined) setText('')
    } catch (e) {
      setError((e as Error).message || 'Prime did not answer; try again.')
    } finally {
      setSending('')
    }
  }

  const messages = thread?.messages ?? []
  const shown = pages?.find((p) => p.id === openPage && !p.deleted_at)
  const docked = wide && !!shown
  return (
    <div className={cx(docked && 'flex items-start gap-4')} data-testid="member-prime-layout">
      <div className={cx('pb-40', docked && 'min-w-0 flex-1')} data-testid="member-prime">
        <PageTitle eyebrow="Prime">Ask Prime</PageTitle>
        <p className="text-xs text-faint mb-4" data-testid="member-prime-notice">
          {thread?.notice ?? 'Alfie can read this thread, and Prime can pass things on to him.'}
        </p>
        <ul className="flex flex-col gap-3">
          {messages.map((m, i) => (
            <li
              key={`${m.at}-${i}`}
              className={cx('max-w-[88%]', m.role === 'user' ? 'self-end' : 'self-start')}
              data-testid={m.role === 'user' ? 'member-prime-you' : 'member-prime-reply'}
            >
              {m.prime && <p className="text-[11px] text-faint mb-0.5">Prime, for Alfie</p>}
              <div
                className={cx(
                  'px-3 py-2',
                  m.role === 'user' ? 'bg-accent text-on-accent' : 'bg-raised text-fg',
                )}
                style={{ borderRadius: 'var(--radius-bubble)' }}
              >
                <MdLite text={m.content} className="text-[15px] leading-relaxed" />
              </div>
              {m.role === 'assistant' && m.pages?.length ? (
                <div className="mt-1.5 flex flex-wrap gap-1.5" data-testid="member-prime-pages">
                  {m.pages.map((id) => {
                    const p = pages?.find((x) => x.id === id && !x.deleted_at)
                    if (!p) return null
                    return (
                      <button
                        key={id}
                        type="button"
                        onClick={() => setOpenPage(id)}
                        className="flex min-h-9 max-w-full items-center gap-1.5 rounded-full border border-line bg-panel px-3 text-sm text-fg active:scale-95 transition-transform"
                        data-testid="member-prime-page"
                      >
                        <FileText size={14} aria-hidden />
                        <span className="truncate">{p.name}</span>
                      </button>
                    )
                  })}
                </div>
              ) : null}
              {m.role === 'assistant' && i === messages.length - 1 && !sending && m.options?.length ? (
                <div className="mt-1.5 flex flex-wrap gap-1.5" data-testid="member-prime-options">
                  {m.options.map((o) => (
                    <button
                      key={o}
                      type="button"
                      onClick={() => void send(o)}
                      className="min-h-9 rounded-full bg-accent-soft px-3.5 text-sm text-fg active:scale-95 transition-transform"
                      data-testid="member-prime-option"
                    >
                      {o}
                    </button>
                  ))}
                </div>
              ) : null}
            </li>
          ))}
          {sending && (
            <>
              <li
                className="self-end max-w-[88%] bg-accent text-on-accent px-3 py-2 text-[15px]"
                style={{ borderRadius: 'var(--radius-bubble)' }}
              >
                {sending}
              </li>
              <li className="self-start text-sm text-faint" data-testid="member-prime-thinking">
                Prime is on it…
              </li>
            </>
          )}
        </ul>
        {error && (
          <p className="mt-3 text-sm text-fg-2" role="alert" data-testid="member-prime-error">
            {error}
          </p>
        )}
        <div ref={end} />
        <div
          className="kb-follow fixed inset-x-3 z-20 md:static md:mt-4 md:inset-auto"
          style={{
            bottom: keyboardUp ? 'calc(var(--vv-gap) + 0.5rem)' : 'calc(var(--above-tabbar) + 0.75rem)',
          }}
        >
          <div
            className="flex items-end gap-2 bg-panel/95 border border-line p-1.5"
            style={{ borderRadius: 'var(--radius-composer)' }}
          >
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={1}
              maxLength={1500}
              className="bare-input flex-1 min-w-0 px-3 py-2 resize-none max-h-40 leading-snug"
              style={{ borderRadius: 'var(--radius-composer-inner)' }}
              placeholder="Tell Prime what's up…"
              data-testid="member-prime-input"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey && !window.matchMedia('(pointer: coarse)').matches) {
                  e.preventDefault()
                  void send()
                }
              }}
            />
            <button
              type="button"
              onClick={() => void send()}
              disabled={!text.trim() || !!sending}
              className="h-10 w-10 shrink-0 rounded-full bg-accent text-on-accent flex items-center justify-center disabled:opacity-30 active:scale-95 transition-transform"
              aria-label="Send"
              data-testid="member-prime-send"
            >
              <Send size={18} aria-hidden />
            </button>
          </div>
        </div>
      </div>
      {docked && shown && (
        <DockAside width={width} onWidth={setWidth} label="Pages" testId="member-prime-dock">
          <PageBar name={shown.name} id={shown.id} onClose={() => setOpenPage('')} />
          <div className="min-h-0 flex-1 overflow-y-auto py-3 pr-2">
            <ArtifactEditor key={shown.id} page={shown} />
          </div>
        </DockAside>
      )}
      {!wide && (
        <Sheet open={!!shown} onClose={() => setOpenPage('')} title={shown?.name ?? 'Page'}>
          {shown && (
            <div className="-mx-4" data-testid="member-prime-sheet">
              <ArtifactEditor key={shown.id} page={shown} />
            </div>
          )}
        </Sheet>
      )}
    </div>
  )
}

/** The head of the docked page: its name, a way to open it as a page, and a way to put it away. */
const PageBar = ({ name, id, onClose }: { name: string; id: string; onClose: () => void }) => (
  <header className="flex items-center gap-1 border-b border-line px-3 py-1.5">
    <span className="min-w-0 flex-1 truncate text-sm text-fg">{name}</span>
    <Link
      to="/pages/$id"
      params={{ id }}
      className="grid size-8 shrink-0 place-items-center rounded-full text-muted hover:text-fg"
      aria-label="Open as a page"
    >
      <ExternalLink size={15} aria-hidden />
    </Link>
    <button
      type="button"
      onClick={onClose}
      className="grid size-8 shrink-0 place-items-center rounded-full text-muted hover:text-fg"
      aria-label="Put the page away"
      data-testid="member-prime-dock-close"
    >
      <X size={16} aria-hidden />
    </button>
  </header>
)
